import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchPeriodHistory, fetchHormoneHistory } from '../../lib/userDataService';

/**
 * RecentCyclesScreen — Theo's memory of the user's body across cycles.
 *
 * NOT a database table. This is editorial storytelling with progressive
 * disclosure. Each cycle is reconstructed by joining `period_history`
 * with the `symptom_logs` and `daily_checkins` that fell inside its
 * window, then interpreted into a short, human narrative — including
 * a Theo-assigned *title* ("The Recovery Cycle", "The Stable Cycle").
 *
 * Data sources (all existing tables — zero migrations):
 *   • period_history   — start_date, end_date, duration
 *   • symptom_logs     — 90-day window
 *   • daily_checkins   — 90-day window (mood, energy_level, pain_level)
 *   • hormone_reports  — most-recent 5 (optional overlay)
 *
 * Design language:
 *   • .cx-intel-hero + .cx-intel-section chrome (matches Timeline/Reports)
 *   • Progressive disclosure: collapsed card → expanded story with spring
 *     motion via CSS grid-template-rows animation
 *   • Theo title as the primary headline for each card
 */

const DAY_MS = 86400_000;

const isoDaysAgo = (n) => new Date(Date.now() - n * DAY_MS).toISOString().split('T')[0];
const asDate = (iso) => new Date(iso);
const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
const fmtLong = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
const round1 = (n) => Math.round(n * 10) / 10;

/* ─── Symptom prettifier (matches other cycle screens) ─── */
const PRETTY_SYMPTOM = {
  acne: 'Acne', hair_loss: 'Hair loss', mood: 'Mood shifts', fatigue: 'Fatigue',
  sleep_quality: 'Poor sleep', weight_change: 'Weight change',
  cramps: 'Cramps', bloating: 'Bloating', headache: 'Headache',
};

/* ─── Theo title assignment ────────────────────────────────
 *  Order matters — most specific label wins. */
function assignTitle(c) {
  if (c.isCurrent)                        return 'The Current Cycle';
  if (c.cycleLength >= 40)                return 'The Extended Cycle';
  if (c.cycleLength >= 35)                return 'The Long Cycle';
  if (c.cycleLength <= 22 && c.cycleLength > 0) return 'The Short Cycle';
  if (c.recoveryTrend >= 12)              return 'The Recovery Cycle';
  if (c.recoveryAvg >= 78 && c.symptomLoad <= 6)  return 'The Best Cycle Yet';
  if (c.recoveryAvg <= 45)                return 'The Low-Energy Cycle';
  if (c.symptomLoad >= 18)                return 'The Symptom-Heavy Cycle';
  if (c.moodAvg != null && c.moodAvg <= 3.5) return 'The Tender Cycle';
  if (c.moodAvg != null && c.moodAvg >= 7.5) return 'The Bright Cycle';
  if (Math.abs(c.cycleLength - 28) <= 1 && c.symptomLoad <= 10) return 'The Stable Cycle';
  if (c.periodDuration >= 7)              return 'The Heavy-Flow Cycle';
  if (c.periodDuration <= 3 && c.periodDuration > 0) return 'The Light-Flow Cycle';
  return 'A Quiet Cycle';
}

/* ─── Confidence indicator ────────────────────────────────
 *  Based on how much data we have for this window. */
function assignConfidence(c) {
  const daysCovered = c.symptomDays + c.checkinDays;
  if (daysCovered >= 18) return { label: 'High',   dots: 3 };
  if (daysCovered >= 10) return { label: 'Medium', dots: 2 };
  if (daysCovered >= 4)  return { label: 'Low',    dots: 1 };
  return { label: 'Sparse', dots: 0 };
}

/* ─── Build cycles from period_history ────────────────────
 *  A cycle = window starting at period.start_date and ending
 *  on the day before the NEXT period's start_date. The most
 *  recent (open) cycle ends today. */
function buildCycles({ periods, symptoms, checkins }) {
  if (!periods?.length) return [];
  // periods is ordered by start_date DESC — reverse for chronological build
  const sorted = [...periods].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  const cycles = sorted.map((p, i) => {
    const startD = asDate(p.start_date);
    const nextP  = sorted[i + 1];
    const endD   = nextP ? new Date(asDate(nextP.start_date).getTime() - DAY_MS) : new Date();
    const cycleLength = Math.max(1, Math.round((endD.getTime() - startD.getTime()) / DAY_MS) + 1);

    const inWindow = (isoDate) => {
      const t = asDate(isoDate).getTime();
      return t >= startD.getTime() && t <= endD.getTime();
    };

    const symWin = symptoms.filter(r => inWindow(r.log_date));
    const chkWin = checkins.filter(r => inWindow(r.log_date || r.checkin_date));

    // Symptom aggregation
    const symBuckets = {};
    const symKeys = ['acne', 'hair_loss', 'mood', 'fatigue', 'sleep_quality', 'weight_change'];
    symKeys.forEach(k => {
      const total = symWin.reduce((s, r) => s + (r[k] || 0), 0);
      if (total > 0) symBuckets[k] = total;
    });
    // Also include JSONB symptoms from checkins (cramps, bloating, etc)
    chkWin.forEach(c => {
      const s = c.symptoms || {};
      Object.entries(s).forEach(([k, v]) => {
        const n = Number(v) || 0;
        if (n > 0) symBuckets[k] = (symBuckets[k] || 0) + n;
      });
    });
    const topSymptoms = Object.entries(symBuckets)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([k, v]) => ({ key: k, total: v, label: PRETTY_SYMPTOM[k] || k.replace(/_/g, ' ') }));

    // Recovery
    const recScores = symWin.map(r => {
      const f = Math.max(0, Math.min(10, r.fatigue ?? 5));
      const s = Math.max(0, Math.min(10, r.sleep_quality ?? 5));
      return Math.round(((10 - f) + s) / 2 * 10);
    });
    const recoveryAvg = Math.round(avg(recScores));
    const recoveryTrend = recScores.length >= 4
      ? Math.round(avg(recScores.slice(-Math.ceil(recScores.length / 2))) - avg(recScores.slice(0, Math.floor(recScores.length / 2))))
      : 0;

    // Mood + energy from checkins
    const moods    = chkWin.map(c => c.mood).filter(v => v != null);
    const energies = chkWin.map(c => c.energy_level).filter(v => v != null);
    const moodAvg   = moods.length ? round1(avg(moods)) : null;
    const energyAvg = energies.length ? round1(avg(energies)) : null;

    const symptomLoad = topSymptoms.reduce((s, x) => s + x.total, 0);

    return {
      idx: i + 1,
      startDate: startD,
      endDate: endD,
      startISO: p.start_date,
      cycleLength,
      periodDuration: p.duration || (p.end_date ? Math.max(1, Math.round((asDate(p.end_date) - startD) / DAY_MS) + 1) : null),
      flowIntensity: p.flow_intensity || null,
      isCurrent: !nextP,
      symptomDays: symWin.length,
      checkinDays: chkWin.length,
      topSymptoms,
      symptomLoad,
      recoveryAvg,
      recoveryTrend,
      moodAvg,
      energyAvg,
    };
  });

  // Second pass: build comparison with previous, title, confidence, observation
  cycles.forEach((c, i) => {
    const prev = cycles[i - 1];
    c.title = assignTitle(c);
    c.confidence = assignConfidence(c);
    c.observation = buildObservation(c, prev);
    c.comparison  = prev ? buildComparison(c, prev) : null;
  });

  // Return newest first — that's what users want to see
  return cycles.reverse();
}

function buildObservation(c, prev) {
  if (c.isCurrent) return 'Theo is still watching this one — patterns will lock as it closes.';
  if (c.recoveryTrend >= 12) return 'Theo noticed your recovery improved as the cycle progressed.';
  if (c.recoveryTrend <= -12) return 'Recovery dropped through this cycle — worth softening the next one.';
  if (prev && Math.abs(c.cycleLength - prev.cycleLength) <= 1) return 'Cycle length held steady — a sign of hormonal rhythm.';
  if (c.topSymptoms[0]) return `${c.topSymptoms[0].label} was your most common symptom.`;
  if (c.recoveryAvg >= 75) return 'Your body ran well-supplied through this window.';
  if (c.energyAvg != null && c.energyAvg >= 7) return 'Energy sat consistently above your baseline.';
  return 'Steady — nothing pulled Theo\u2019s attention this cycle.';
}

function buildComparison(c, prev) {
  const dLen = c.cycleLength - prev.cycleLength;
  const dRec = c.recoveryAvg - prev.recoveryAvg;
  const parts = [];
  if (Math.abs(dLen) >= 2) parts.push(`${dLen > 0 ? '+' : ''}${dLen} days vs previous`);
  if (Math.abs(dRec) >= 6) parts.push(`recovery ${dRec > 0 ? 'up' : 'down'} ${Math.abs(dRec)}pt`);
  if (c.topSymptoms[0] && prev.topSymptoms[0] && c.topSymptoms[0].key !== prev.topSymptoms[0].key) {
    parts.push(`primary symptom shifted from ${prev.topSymptoms[0].label.toLowerCase()} to ${c.topSymptoms[0].label.toLowerCase()}`);
  }
  return parts.length ? parts.join(' · ') : 'Very similar shape to the previous cycle.';
}

/* ─── Cross-cycle summary (top of page) ─── */
function buildSummary(cycles) {
  if (cycles.length < 2) return null;
  const closed = cycles.filter(c => !c.isCurrent);
  if (closed.length < 1) return null;
  const avgLen  = round1(avg(closed.map(c => c.cycleLength)));
  const avgPer  = round1(avg(closed.map(c => c.periodDuration).filter(Boolean)));
  const recVals = closed.map(c => c.recoveryAvg).filter(v => v > 0);
  const recAvg  = Math.round(avg(recVals));
  // Symptom aggregation across all closed cycles
  const symBuckets = {};
  closed.forEach(c => c.topSymptoms.forEach(s => {
    symBuckets[s.key] = (symBuckets[s.key] || { key: s.key, label: s.label, total: 0 });
    symBuckets[s.key].total += s.total;
  }));
  const topSymptoms = Object.values(symBuckets).sort((a, b) => b.total - a.total).slice(0, 3);
  // Consistency = std deviation of cycle length
  const lens = closed.map(c => c.cycleLength);
  const mean = avg(lens);
  const variance = avg(lens.map(l => (l - mean) ** 2));
  const stdDev = Math.sqrt(variance);
  const consistency = stdDev <= 2 ? 'Very consistent' : stdDev <= 5 ? 'Consistent' : stdDev <= 9 ? 'Irregular' : 'Highly variable';

  // Recovery trend across cycles (newest vs oldest half)
  const recentHalf = closed.slice(0, Math.ceil(closed.length / 2));
  const olderHalf  = closed.slice(Math.floor(closed.length / 2));
  const recentRec = avg(recentHalf.map(c => c.recoveryAvg));
  const olderRec  = avg(olderHalf.map(c => c.recoveryAvg));
  const recoveryDelta = Math.round(recentRec - olderRec);
  const recoveryTrend = recoveryDelta >= 6 ? 'Improving' : recoveryDelta <= -6 ? 'Declining' : 'Steady';

  const theoLine = (() => {
    if (consistency.startsWith('Very') && recoveryTrend === 'Improving') return 'Your rhythm is settling and your body is recovering better — a strong 3-month picture.';
    if (consistency === 'Highly variable') return 'Cycle length varies week to week — Theo is watching for the trigger.';
    if (recoveryTrend === 'Declining') return 'Recovery has softened across the recent cycles — worth protecting sleep and stress.';
    if (topSymptoms[0]) return `${topSymptoms[0].label} keeps showing up — worth exploring with your clinician.`;
    return 'Your body is telling a steady story across these cycles.';
  })();

  return {
    cycleCount: closed.length,
    avgLen,
    avgPer,
    recAvg,
    topSymptoms,
    consistency,
    recoveryTrend,
    recoveryDelta,
    theoLine,
  };
}

/* ─── Fetch layer ─── */
async function fetchSupportingData(userId) {
  if (!userId) return { periods: [], symptoms: [], checkins: [], hormones: [] };
  const from = isoDaysAgo(365);
  const [{ data: symptoms }, { data: checkins }] = await Promise.all([
    supabase.from('symptom_logs').select('log_date, acne, hair_loss, mood, fatigue, sleep_quality, weight_change')
      .eq('user_id', userId).gte('log_date', from).order('log_date', { ascending: true }),
    supabase.from('daily_checkins').select('checkin_date, log_date, mood, energy_level, pain_level, symptoms')
      .eq('user_id', userId).gte('checkin_date', from).order('checkin_date', { ascending: true }),
  ]);
  const [periods, hormones] = await Promise.all([
    fetchPeriodHistory(userId),
    fetchHormoneHistory(userId),
  ]);
  return { periods: periods || [], symptoms: symptoms || [], checkins: checkins || [], hormones: hormones || [] };
}

/* ═════════════════════════════════════════════════════════════════
   SCREEN
   ═════════════════════════════════════════════════════════════════ */

export default function RecentCyclesScreen({ userId }) {
  const [state, setState] = useState({ loading: true, cycles: [], hormones: [] });
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userId) { if (alive) setState({ loading: false, cycles: [], hormones: [] }); return; }
      const { periods, symptoms, checkins, hormones } = await fetchSupportingData(userId);
      if (!alive) return;
      const cycles = buildCycles({ periods, symptoms, checkins });
      setState({ loading: false, cycles, hormones });
    })();
    return () => { alive = false; };
  }, [userId]);

  const summary = useMemo(() => buildSummary(state.cycles), [state.cycles]);

  const heroSentence = state.loading
    ? 'Theo is remembering your body\u2026'
    : state.cycles.length === 0
      ? 'Log a period to start your cycle memory.'
      : state.cycles.length === 1
        ? 'One cycle in. The story is just starting.'
        : `${state.cycles.length} cycles remembered by Theo.`;

  return (
    <div className="cx-sub-body" data-testid="recent-cycles-screen">
      {/* HERO */}
      <section className="cx-intel-hero">
        <div className="cx-intel-eyebrow">MEMORY · YOUR RECENT CYCLES</div>
        <h1 className="cx-intel-h1">The story of your body <em>told across cycles.</em></h1>
        <p className="cx-sentence" style={{ marginTop: 14, maxWidth: 340 }}>{heroSentence}</p>
      </section>

      {/* ACROSS YOUR RECENT CYCLES */}
      {summary && (
        <section className="cx-intel-section" data-testid="cycles-summary">
          <div className="cx-intel-label">Across your recent cycles</div>
          <div className="cx-rc-summary">
            <div className="cx-rc-summary-grid">
              <SummaryKV k="Average cycle" v={`${summary.avgLen} days`} />
              <SummaryKV k="Average period" v={summary.avgPer ? `${summary.avgPer} days` : '—'} />
              <SummaryKV k="Recovery" v={`${summary.recAvg}%`} />
              <SummaryKV k="Consistency" v={summary.consistency} />
              <SummaryKV k="Trend" v={summary.recoveryTrend} />
              <SummaryKV k="Cycles seen" v={String(summary.cycleCount)} />
            </div>
            {summary.topSymptoms.length > 0 && (
              <div className="cx-rc-summary-syms">
                <span className="cx-rc-summary-syms-lbl">Most common</span>
                <div className="cx-rc-summary-syms-list">
                  {summary.topSymptoms.map(s => (
                    <span key={s.key} className="cx-rc-chip">{s.label}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="cx-rc-summary-theo">
              <span className="cx-theo-tag">THEO</span> {summary.theoLine}
            </div>
          </div>
        </section>
      )}

      {/* CYCLES LIST */}
      <section className="cx-intel-section">
        <div className="cx-intel-label">Cycle by cycle</div>

        {state.loading ? (
          <EmptyBlock text="Reading your history…" />
        ) : state.cycles.length === 0 ? (
          <EmptyBlock text="Once you log your first period, Theo will start building your cycle memory here." />
        ) : (
          <div className="cx-rc-list" data-testid="cycles-list">
            {state.cycles.map((c, i) => (
              <CycleCard
                key={c.startISO + i}
                cycle={c}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? null : i)}
                hormones={state.hormones}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════
   ATOMS
   ═════════════════════════════════════════════════════════════════ */

function SummaryKV({ k, v }) {
  return (
    <div className="cx-rc-summary-kv">
      <span className="cx-rc-summary-kv-k">{k}</span>
      <span className="cx-rc-summary-kv-v">{v}</span>
    </div>
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="cx-glass cx-empty-block" style={{ padding: 18, borderRadius: 20 }}>
      <p style={{ margin: 0, fontSize: 13.5, color: 'var(--cx-mute)', lineHeight: 1.55 }}>{text}</p>
    </div>
  );
}

function ConfidenceRow({ confidence }) {
  return (
    <div className="cx-rc-conf">
      <span className="cx-rc-conf-lbl">CONFIDENCE · {confidence.label}</span>
      <span className="cx-rc-conf-dots">
        {[0, 1, 2].map(i => (
          <span key={i} className={`cx-rc-conf-dot ${i < confidence.dots ? 'cx-on' : ''}`} />
        ))}
      </span>
    </div>
  );
}

function CycleCard({ cycle, open, onToggle, hormones }) {
  const c = cycle;
  const dateRange = `${fmt(c.startDate)} – ${fmt(c.endDate)}`;
  const dominant = c.topSymptoms[0];
  const moodSummary = moodEnergyLine(c);

  return (
    <div
      className={`cx-rc-card ${open ? 'cx-rc-open' : ''}`}
      data-testid={`rc-card-${c.idx}`}
    >
      <button
        className="cx-rc-card-head"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`rc-toggle-${c.idx}`}
      >
        <div className="cx-rc-head-l">
          <div className="cx-rc-eyebrow">
            {c.isCurrent ? 'CURRENT · CYCLE ' : 'CYCLE '}#{c.idx}
          </div>
          <h3 className="cx-rc-title">{c.title}</h3>
          <div className="cx-rc-range">
            {dateRange} <span className="cx-rc-dot">·</span> {c.cycleLength} days
          </div>

          <div className="cx-rc-glance">
            {dominant && (
              <p className="cx-rc-glance-line">
                <span className="cx-rc-glance-em">{dominant.label}</span> was your most common symptom.
              </p>
            )}
            {moodSummary && (
              <p className="cx-rc-glance-line">
                {moodSummary}
              </p>
            )}
            <p className="cx-rc-glance-theo">
              <span className="cx-theo-tag">THEO</span> {c.observation}
            </p>
          </div>

          <ConfidenceRow confidence={c.confidence} />
        </div>

        <div className="cx-rc-head-r">
          <svg
            className={`cx-rc-chev ${open ? 'cx-rot' : ''}`}
            width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="1.6"
          >
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </div>
      </button>

      {/* Expanded body — grid-template-rows animation for spring reveal */}
      <div className="cx-rc-expand">
        <div className="cx-rc-expand-inner">
          <ExpandedBody cycle={c} hormones={hormones} />
        </div>
      </div>
    </div>
  );
}

function moodEnergyLine(c) {
  if (c.moodAvg == null && c.energyAvg == null) return null;
  const bits = [];
  if (c.energyAvg != null) {
    const e = c.energyAvg;
    bits.push(e >= 7 ? 'energy ran high' : e <= 3.5 ? 'energy stayed low' : 'energy sat around baseline');
  }
  if (c.moodAvg != null) {
    const m = c.moodAvg;
    bits.push(m >= 7 ? 'mood felt bright' : m <= 3.5 ? 'mood ran tender' : 'mood held steady');
  }
  if (!bits.length) return null;
  return bits.join(' · ') + '.';
}

/* ─── Expanded body — Theo's detailed reading ─── */
function ExpandedBody({ cycle: c, hormones }) {
  const hInCycle = (hormones || []).filter(h => {
    const t = new Date(h.created_at).getTime();
    return t >= c.startDate.getTime() && t <= c.endDate.getTime();
  });

  return (
    <div className="cx-rc-detail">
      {/* Timeline mini */}
      <DetailBlock label="Timeline">
        <div className="cx-rc-mini-timeline">
          <MiniStripe day="Day 1"                  text="Bleed begins" />
          {c.periodDuration && <MiniStripe day={`Day ${c.periodDuration}`} text="Period ends" />}
          <MiniStripe day={`Day ~${Math.round(c.cycleLength / 2)}`} text="Mid-cycle window" />
          <MiniStripe day={`Day ${c.cycleLength}`} text={c.isCurrent ? 'Today' : 'Next cycle begins'} />
        </div>
      </DetailBlock>

      {/* Symptoms */}
      <DetailBlock label="Symptoms throughout the cycle">
        {c.topSymptoms.length > 0 ? (
          <div className="cx-rc-detail-list">
            {c.topSymptoms.map(s => (
              <div key={s.key} className="cx-rc-detail-row" data-testid={`rc-sym-${s.key}`}>
                <span className="cx-rc-detail-row-k">{s.label}</span>
                <span className="cx-rc-detail-row-v">load {s.total}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="cx-fine-print">No symptoms logged during this cycle.</p>
        )}
      </DetailBlock>

      {/* Recovery */}
      <DetailBlock label="Recovery">
        {c.recoveryAvg > 0 ? (
          <div className="cx-rc-detail-inline">
            <span className="cx-rc-big">{c.recoveryAvg}<span className="cx-rc-u">%</span></span>
            <span className="cx-rc-detail-note">
              average · trend {c.recoveryTrend > 0 ? '+' : ''}{c.recoveryTrend}pt across the cycle
            </span>
          </div>
        ) : (
          <p className="cx-fine-print">No recovery signal — sleep and fatigue weren&rsquo;t logged this cycle.</p>
        )}
      </DetailBlock>

      {/* Mood & Energy */}
      <DetailBlock label="Mood & energy">
        <div className="cx-rc-detail-list">
          <div className="cx-rc-detail-row">
            <span className="cx-rc-detail-row-k">Mood</span>
            <span className="cx-rc-detail-row-v">{c.moodAvg != null ? `${c.moodAvg}/10` : 'Not tracked'}</span>
          </div>
          <div className="cx-rc-detail-row">
            <span className="cx-rc-detail-row-k">Energy</span>
            <span className="cx-rc-detail-row-v">{c.energyAvg != null ? `${c.energyAvg}/10` : 'Not tracked'}</span>
          </div>
        </div>
      </DetailBlock>

      {/* Period details */}
      <DetailBlock label="Period details">
        <div className="cx-rc-detail-list">
          <div className="cx-rc-detail-row">
            <span className="cx-rc-detail-row-k">Started</span>
            <span className="cx-rc-detail-row-v">{fmtLong(c.startDate)}</span>
          </div>
          <div className="cx-rc-detail-row">
            <span className="cx-rc-detail-row-k">Duration</span>
            <span className="cx-rc-detail-row-v">{c.periodDuration ? `${c.periodDuration} days` : 'Unlogged'}</span>
          </div>
          {c.flowIntensity && (
            <div className="cx-rc-detail-row">
              <span className="cx-rc-detail-row-k">Flow</span>
              <span className="cx-rc-detail-row-v" style={{ textTransform: 'capitalize' }}>{c.flowIntensity}</span>
            </div>
          )}
        </div>
      </DetailBlock>

      {/* Hormones in-window */}
      {hInCycle.length > 0 && (
        <DetailBlock label="Hormone reports">
          <div className="cx-rc-detail-list">
            {hInCycle.map((h, i) => (
              <div key={h.id || i} className="cx-rc-detail-row">
                <span className="cx-rc-detail-row-k">
                  {new Date(h.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
                <span className="cx-rc-detail-row-v">
                  {h.lh && h.fsh ? `LH ${h.lh} / FSH ${h.fsh}` : 'Panel uploaded'}
                </span>
              </div>
            ))}
          </div>
        </DetailBlock>
      )}

      {/* Theo detailed analysis */}
      <DetailBlock label="Theo's detailed analysis">
        <p className="cx-rc-analysis">
          {buildDetailedAnalysis(c)}
        </p>
      </DetailBlock>

      {/* Comparison */}
      {c.comparison && (
        <DetailBlock label="Compared with the previous cycle">
          <p className="cx-rc-analysis">
            <span className="cx-rc-analysis-em">{c.comparison}.</span>
          </p>
        </DetailBlock>
      )}
    </div>
  );
}

function DetailBlock({ label, children }) {
  return (
    <div className="cx-rc-detail-block">
      <div className="cx-rc-detail-lbl">{label}</div>
      {children}
    </div>
  );
}

function MiniStripe({ day, text }) {
  return (
    <div className="cx-rc-mini-stripe">
      <span className="cx-rc-mini-day">{day}</span>
      <span className="cx-rc-mini-text">{text}</span>
    </div>
  );
}

function buildDetailedAnalysis(c) {
  const parts = [];
  if (c.isCurrent) {
    parts.push('This cycle is still open. Theo is still learning its shape.');
  } else {
    parts.push(`This cycle ran ${c.cycleLength} days — ${c.cycleLength >= 32 ? 'longer than a typical 28-day window, but well within a PCOS-normal range' : c.cycleLength <= 24 ? 'shorter than average; worth noting if it repeats' : 'squarely within a healthy rhythm'}.`);
  }
  if (c.recoveryAvg > 0) {
    parts.push(`Recovery averaged ${c.recoveryAvg}% ${c.recoveryTrend >= 8 ? 'and climbed as the cycle progressed' : c.recoveryTrend <= -8 ? 'and softened toward the end' : 'with little variation'}.`);
  }
  if (c.topSymptoms[0]) {
    parts.push(`${c.topSymptoms[0].label} was the dominant symptom — logged ${c.topSymptoms[0].total} times.`);
  }
  if (c.periodDuration) {
    parts.push(`The bleed lasted ${c.periodDuration} days${c.flowIntensity ? ` with ${c.flowIntensity.toLowerCase()} flow` : ''}.`);
  }
  return parts.join(' ');
}
